export const MARKER_SIZE = { width: 36, height: 44 };

declare global {
  interface Window {
    google?: typeof google;
  }
}

let mapsLoadPromise: Promise<typeof google.maps> | null = null;

/**
 * Singleton Google Maps JS loader (places library).
 * Safe to call from many components — one script, one in-flight promise.
 */
export function loadGoogleMapsScript(apiKey: string): Promise<typeof google.maps> {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is missing'));
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps);
  }

  if (mapsLoadPromise) return mapsLoadPromise;

  mapsLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google.maps);
      return;
    }

    const finish = () => {
      if (window.google?.maps?.places) {
        resolve(window.google.maps);
        return;
      }
      mapsLoadPromise = null;
      reject(new Error('Google Maps Places library failed to load'));
    };

    const existingScript = document.getElementById(
      'google-maps-script',
    ) as HTMLScriptElement | null;

    if (existingScript) {
      if (window.google?.maps) {
        finish();
        return;
      }
      existingScript.addEventListener('load', finish, { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          mapsLoadPromise = null;
          reject(new Error('Google Maps failed to load'));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => {
      mapsLoadPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return mapsLoadPromise;
}

let markerIconPromise: Promise<string> | null = null;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

export function createScreenistaMarkerIcon(): Promise<string> {
  if (markerIconPromise) return markerIconPromise;

  markerIconPromise = (async () => {
    const scale = 3;
    const { width, height } = MARKER_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');

    ctx.scale(scale, scale);
    const cx = width / 2;
    const cy = 14;

    ctx.beginPath();
    ctx.moveTo(cx, 1);
    ctx.bezierCurveTo(10.2, 1, 5, 6.2, 5, 12.5);
    ctx.bezierCurveTo(5, 20.7, 18, 41, 18, 41);
    ctx.bezierCurveTo(18, 41, 31, 20.7, 31, 12.5);
    ctx.bezierCurveTo(31, 6.2, 25.8, 1, cx, 1);
    ctx.closePath();
    ctx.fillStyle = '#7C6CF0';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, 8.5, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();

    try {
      const logo = await loadImage('/favicon.svg');
      const logoSize = 15;
      ctx.drawImage(logo, cx - logoSize / 2, cy - logoSize / 2, logoSize, logoSize);
    } catch {
      // Marker remains usable without the logo asset.
    }

    return canvas.toDataURL('image/png');
  })().catch((err) => {
    markerIconPromise = null;
    throw err;
  });

  return markerIconPromise;
}

export function getMarkerIconConfig(
  googleMaps: typeof google.maps,
  markerIconUrl: string,
) {
  return {
    url: markerIconUrl,
    scaledSize: new googleMaps.Size(MARKER_SIZE.width, MARKER_SIZE.height),
    anchor: new googleMaps.Point(MARKER_SIZE.width / 2, MARKER_SIZE.height),
  };
}

export function getUserLocationIcon(googleMaps: typeof google.maps) {
  return {
    path: googleMaps.SymbolPath.CIRCLE,
    scale: 9,
    fillColor: '#4285F4',
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 3,
  };
}

export function getBillboardCoordinates(billboard: {
  latitude?: number | string;
  longitude?: number | string;
  lattitude?: number | string;
}): { lat: number; lng: number } | null {
  const lat = Number(billboard?.latitude ?? billboard?.lattitude);
  const lng = Number(billboard?.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
}
